import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  AttachMoney as PriceIcon,
  Schedule as DurationIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useServicesByCategory } from '../../hooks/useServices';
import { formatDistanceToNow } from 'date-fns';
import DevOnly from '../../components/DevOnly';

const ServiceCategoryView = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Prepare query parameters
  const queryParams = {
    search: searchTerm,
    page: page + 1,
    limit: pageSize,
    status: statusFilter !== 'all' ? statusFilter : undefined
  };

  // Fetch services for this category
  const { data, isLoading, error, refetch } = useServicesByCategory(categoryId, queryParams);

  const services = data?.services || [];
  const totalCount = data?.total || 0;
  const categoryInfo = data?.category_info;

  const totalCategoryAppointments = categoryInfo?.appointment_count ?? services.reduce((sum, s) => sum + (s.appointment_count || 0), 0);
  const recentCategoryAppointments = categoryInfo?.recent_appointment_count ?? services.reduce((sum, s) => sum + (s.recent_appointments || 0), 0);
  const activeServicesCount = categoryInfo?.active_service_count ?? services.filter(s => s.is_active).length;

  // Category display names
  const getCategoryDisplayName = (categoryInfo) => {
    if (!categoryInfo) return `Category ${categoryId}`;
    return categoryInfo.name || `Category ${categoryId}`;
  };

  const getTreatmentTypeFromName = (name) => {
    if (!name) return 'Other Treatments';
    const lowerName = name.toLowerCase();
    if (lowerName.includes('face') || lowerName.includes('facial')) return 'Facial Treatments';
    if (lowerName.includes('body') || lowerName.includes('abdomen') || lowerName.includes('arm')) return 'Body Treatments';
    if (lowerName.includes('laser') || lowerName.includes('ipl')) return 'Laser Treatments';
    if (lowerName.includes('skin') || lowerName.includes('aesthetic')) return 'Skin Care';
    if (lowerName.includes('inject') || lowerName.includes('botox')) return 'Injectable Treatments';
    if (lowerName.includes('consult')) return 'Consultations';
    return 'Other Treatments';
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'Free';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const formatDuration = (duration) => {
    if (!duration) return 'Not specified';
    return `${duration} min`;
  };

  const handleServiceClick = (serviceId) => {
    navigate(`/services/service/${serviceId}`);
  };

  const columns = [
    {
      field: 'service_name',
      headerName: 'Service Name',
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          cursor: 'pointer',
          py: 1
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PriceIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="body2" color="primary" fontWeight={500}>
            {formatPrice(params.value)}
          </Typography>
        </Box>
      )
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DurationIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="body2">
            {formatDuration(params.value)}
          </Typography>
        </Box>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <ActiveIcon fontSize="small" /> : <InactiveIcon fontSize="small" />}
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'appointment_count',
      headerName: 'Appointments',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 0}
        </Typography>
      )
    },
    {
      field: 'recent_appointments',
      headerName: '30 Day',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || 0}
        </Typography>
      )
    },
    {
      field: 'created_date',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value 
            ? formatDistanceToNow(new Date(params.value), { addSuffix: true })
            : 'Unknown'
          }
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            title="View Service"
            onClick={(e) => {
              e.stopPropagation();
              handleServiceClick(params.row.id);
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
          <DevOnly>
            <IconButton
              size="small"
              title="Edit Service"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/services/edit/${params.row.id}`);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </DevOnly>
          <DevOnly>
            <IconButton
              size="small"
              title="Duplicate Service"
              onClick={(e) => {
                e.stopPropagation();
                // Handle duplicate
              }}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </DevOnly>
        </Box>
      )
    }
  ];

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load services: {error.message}
          <Button onClick={() => refetch()} size="small" sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  const categoryName = getCategoryDisplayName(categoryInfo);
  const treatmentType = getTreatmentTypeFromName(categoryName);

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/services" color="inherit">
          Services
        </Link>
        <Typography color="text.primary">{categoryName}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/services')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h4">
              {categoryName}
            </Typography>
            <Chip
              label={treatmentType}
              color="primary"
              variant="outlined"
            />
          </Box>
          <Typography variant="body1" color="text.secondary">
            {totalCount} service{totalCount !== 1 ? 's' : ''} in this category
            {categoryInfo?.code && ` • Code: ${categoryInfo.code}`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/services/create?category=${categoryId}`)}
        >
          Add Service
        </Button>
      </Box>

      {/* Category Stats */}
      {categoryInfo && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="primary">
                  {totalCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Services
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {activeServicesCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Services
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {totalCategoryAppointments}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Appointments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {recentCategoryAppointments}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last 30 Days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="inactive">Inactive Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Services DataGrid */}
      <Paper sx={{ height: 'auto', minHeight: 400 }}>
        <DataGrid
          rows={services}
          columns={columns}
          loading={isLoading}
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          rowCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[25, 50, 100]}
          disableSelectionOnClick
          onRowClick={(params) => handleServiceClick(params.id)}
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'grey.50',
              borderBottom: '1px solid',
              borderBottomColor: 'grey.300',
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default ServiceCategoryView;
